import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, ModalController } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { UserFilterModel, UserModel } from 'src/app/models/User.model';
import { AuthService } from 'src/app/services/auth.service';
import { CheckRolesService } from 'src/app/services/checkRoles/checkRoles.service';
import { EmployeeService } from 'src/app/services/employee-service/employee.service';
import { LanguageService } from 'src/app/services/language/language.service';
import { SharedService } from 'src/app/services/shared/shared.service';
import { UserService } from 'src/app/services/user/user.service';

@Component({
  selector: 'app-profile-modal',
  templateUrl: './profile-modal.component.html',
  styleUrls: ['./profile-modal.component.scss'],
  standalone: false,
})
export class ProfileModalComponent implements OnDestroy {
  constructor(
    private router: Router,
    private employeeService: EmployeeService,
    public modalCtrl: ModalController,
    public lang: LanguageService,
    private storage: Storage,
    private userService: UserService,
    private translate: TranslateService,
    private sharedService: SharedService,
    private alertController: AlertController,
    private checkRolesService: CheckRolesService,
    private authService: AuthService,
  ) {}

  user: UserModel = new UserModel();
  filter: UserFilterModel = new UserFilterModel();
  changePass = false;
  showCurrentPassword = false;
  showNewPassword = false;
  showRepeatPassword = false;
  currentPassword: string = '';
  newPassword: string = '';
  repeatPassword: string = '';
  errorMsg: string = '';
  IdUser: string = '';
  ssoEnabled: boolean = false;
  private suppressSsoToggleEvent = false;
  private ssoAlertOpen = false;

  uppercase = false;
  passLength = false;
  characterSpecial = false;
  characterNumbers = false;
  diffError = false;
  selectedLanguage: string = '';
  loading = true;
  btnEnabled = true;
  changed = false;

  canToggleSso: boolean = false;

  languages: any[] = [
    {
      name: 'Português',
      iso: 'pt',
      translate: 'common.portuguese',
    },
    {
      name: 'Inglês',
      iso: 'en',
      translate: 'common.english',
    },
    {
      name: 'Espanhol',
      iso: 'es',
      translate: 'common.spanish',
    },
  ];

  ngOnDestroy() {
    this.sharedService.notifyOther({ page: 'newsDashboard', disabledSearch: false });
  }

  async ionViewWillEnter() {
    this.loading = true;
    await this.storage.get('employeeId').then(
      async data => {
        await this.getUserInfo(data);
      },
      () => {
        this.loading = false;
      },
    );

    try {
      this.canToggleSso = await this.checkRolesService.checkSsoRole();
    } catch (err) {
      console.warn('Failed to check SSO role', err);
      this.canToggleSso = false;
    }

    this.sharedService.notifyOther({ page: 'newsDashboard', disabledSearch: true });

    await this.storage.get('language').then((res: string) => {
      this.selectedLanguage = res;
    });
  }

  async getUserInfo(user: any) {
    lastValueFrom(this.employeeService.getCollaborator(user)).then(async (user: UserModel) => {
      this.loading = false;
      this.user = user;
      this.suppressSsoToggleEvent = true;
      try {
        if (typeof (this.user as any).ssoEnabled !== 'undefined') {
          this.ssoEnabled = !!(this.user as any).ssoEnabled;
        } else {
          const me = await lastValueFrom(this.userService.getMe());
          this.ssoEnabled = !!(me as any).ssoEnabled;
          (this.user as any).ssoEmail = (this.user as any).ssoEmail || (me as any).ssoEmail;
        }
      } catch {
      } finally {
        setTimeout(() => (this.suppressSsoToggleEvent = false), 0);
      }
    });
  }

  async close(signature = false) {
    await this.modalCtrl.dismiss({ changed: this.changed, signature: signature });
  }

  async changePassword() {
    this.btnEnabled = false;
    let obj = {
      username: this.user.personalNumberSap,
      password: this.newPassword,
      requestPasswordId: '',
      emailResetPassword: '',
      oldPassword: this.currentPassword,
    };

    await this.storage.get('userName').then(res => {
      !obj.username ? (obj.username = res) : null;
    });

    await lastValueFrom(this.userService.resetPassword(obj)).then(
      async () => {
        await this.sharedService.showToast(this.translate.instant('password.changed.success'));
        this.btnEnabled = true;
        await this.close();
      },
      () => {
        this.btnEnabled = true;
      },
    );
  }

  checkPassword() {
    function validateUppercase(pass: string) {
      return /[A-Z]/.test(pass);
    }

    function ValidateCharacterSpecial(pass: string) {
      return /[!@#$%*()_+^&{}}:;?.]/.test(pass);
    }

    function ValidateCharacterNumbers(pass: string) {
      if (/[a-z]/.test(pass) && /[0-9]/.test(pass)) {
        return true;
      } else {
        return false;
      }
    }

    validateUppercase(this.newPassword) ? (this.uppercase = true) : (this.uppercase = false);
    this.newPassword.length >= 6 ? (this.passLength = true) : (this.passLength = false);
    ValidateCharacterSpecial(this.newPassword) ? (this.characterSpecial = true) : (this.characterSpecial = false);
    ValidateCharacterNumbers(this.newPassword) ? (this.characterNumbers = true) : (this.characterNumbers = false);
  }

  checkDiff() {
    if (this.newPassword !== this.repeatPassword) {
      this.diffError = false;
    } else {
      this.diffError = true;
    }
  }

  redirectTo(route: string) {
    this.close();
    this.router.navigate([route]);
  }

  async changeLang() {
    this.changed = true;
    await this.lang.setLanguage(this.selectedLanguage);

    await lastValueFrom(this.userService.setUserLanguage(this.selectedLanguage));
    await this.sharedService.notifyOther({
      refresh: true,
      language: this.selectedLanguage,
    });
  }

  async email() {
    await this.router.navigateByUrl(`panel/network/email-signature`);
    await this.close(true);
  }
}
